import type { LanguagesView, ShareSettings, ShareStatsView } from "../api.js";

/** The address people open: the app's own origin, then the player's path. */
export function linkUrl(origin: string, slug: string, lang: string | null): string {
  return `${origin}/v/${slug}${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`;
}

/** The snippet to paste into a page. `lang` fixes the language the player starts in. */
export function embedCode(origin: string, slug: string, lang: string | null): string {
  const src = `${origin}/e/${slug}${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`;
  return `<iframe src="${src}" style="width:100%;aspect-ratio:16/9;border:0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
}

/**
 * Said under the version picker when the version that was chosen is gone and
 * another is playing in its place. Silent for a demo, and for a share that
 * never chose one (it follows the newest).
 */
export function versionNote(version: ShareSettings["version"]): string | null {
  if (!version?.chosen || !version.playing) return null;
  if (version.chosen.id === version.playing.id) return null;
  return `v${version.chosen.number} was removed, now sharing v${version.playing.number}`;
}

/**
 * What the share section reads again on: the English film it plays and how
 * many languages have a video. An export, a language export or a new version
 * changes it, and the settings (can publish, languages, versions) with it.
 */
export function shareRefreshKey(film: string | null, view: Pick<LanguagesView, "languages"> | null): string {
  const exported = (view?.languages ?? []).filter((language) => language.downloads.video).length;
  return `${film ?? ""}:${exported}`;
}

export type DropOffRow = { label: "25%" | "50%" | "75%" | "100%"; count: number; percent: number };

/** How many views reached each quarter, and what share of all views that is. */
export function dropOff(stats: Pick<ShareStatsView, "views" | "reached">): DropOffRow[] {
  const rows = [
    ["25%", 25],
    ["50%", 50],
    ["75%", 75],
    ["100%", 100],
  ] as const;
  return rows.map(([label, quarter]) => {
    const count = stats.reached[quarter];
    return { label, count, percent: stats.views > 0 ? Math.round((count / stats.views) * 100) : 0 };
  });
}

/** The CSV's file name: the project's, without anything a file name cannot hold. */
export function emailsFilename(projectName: string): string {
  const name = projectName.replace(/[/\\:*?"<>|]/g, "").trim();
  return `${name || "project"}-emails.csv`;
}

/** The language the player is made in, which `startIn: "source"` means. */
export const SOURCE_LANGUAGE = "en";

/**
 * The one "Starts in" select, which stands for two settings: the saved
 * `startIn` (the viewer's language, else English; or always English) and a
 * language fixed in the copied link and embed code (`?lang=`), which is kept
 * in this browser only. "viewer" and "source" are the saved setting;
 * `lang:<code>` is a language of its own.
 */
export type StartsIn = "viewer" | "source" | `lang:${string}`;

/** What the select offers: the viewer's language, English, then every other exported language. */
export function startsInOptions(languages: readonly { code: string; nativeName: string }[]): {
  value: StartsIn;
  label: string;
  lang?: string;
}[] {
  return [
    { value: "viewer", label: "Viewer's language" },
    { value: "source", label: "English" },
    ...languages
      .filter((language) => language.code !== SOURCE_LANGUAGE)
      .map((language) => ({
        value: `lang:${language.code}` as StartsIn,
        label: language.nativeName,
        lang: language.code,
      })),
  ];
}

/**
 * What the select shows. A language fixed in this browser shows only while
 * it is still exported and the saved setting is the viewer's language — the
 * one it was chosen under; otherwise the saved setting does.
 */
export function startsInValue(
  startIn: "viewer" | "source",
  lang: string | null,
  languages: readonly { code: string }[],
): StartsIn {
  if (startIn === "source") return "source";
  if (lang && lang !== SOURCE_LANGUAGE && languages.some((language) => language.code === lang)) return `lang:${lang}`;
  return "viewer";
}

/** What a choice in the select means: the setting to save, and the language for the link and the embed code. */
export function startsInChoice(value: StartsIn): { startIn: "viewer" | "source"; lang: string | null } {
  if (value === "source") return { startIn: "source", lang: null };
  if (value === "viewer") return { startIn: "viewer", lang: null };
  return { startIn: "viewer", lang: value.slice("lang:".length) };
}
